import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from "react";
import client, { setAccessToken, getAccessToken } from "../api/client";
import { disconnectSocket } from "../api/socket";

interface User {
  id: string;
  email: string;
  username: string;
  displayName: string | null;
  avatarUrl: string | null;
  role: string;
  totalPoints: number;
  teamId: string | null;
  teamName: string | null;
  twoFactorEnabled: boolean;
  createdAt: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<{ requires2FA?: boolean; tempToken?: string }>;
  login2FA: (tempToken: string, code: string) => Promise<void>;
  register: (data: {
    email: string;
    username: string;
    password: string;
    displayName?: string;
  }) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  updateUser: (patch: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    try {
      const token = getAccessToken();
      if (!token) {
        setUser(null);
        return;
      }
      const res = await client.get("/auth/me");
      if (res.data?.success) {
        setUser(res.data.data);
      } else {
        setUser(null);
        setAccessToken(null);
      }
    } catch {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    refreshUser().finally(() => setLoading(false));
  }, [refreshUser]);

  const login = useCallback(async (email: string, password: string) => {
    const res = await client.post("/auth/login", { email, password });
    const data = res.data.data;
    if (data.requires2FA) {
      return { requires2FA: true, tempToken: data.tempToken };
    }
    setAccessToken(data.accessToken);
    await refreshUser();
    return {};
  }, [refreshUser]);

  const login2FA = useCallback(async (tempToken: string, code: string) => {
    const res = await client.post("/auth/2fa/complete", { tempToken, code });
    setAccessToken(res.data.data.accessToken);
    await refreshUser();
  }, [refreshUser]);

  const register = useCallback(async (data: {
    email: string;
    username: string;
    password: string;
    displayName?: string;
  }) => {
    const res = await client.post("/auth/register", data);
    setAccessToken(res.data.data.accessToken);
    await refreshUser();
  }, [refreshUser]);

  const logout = useCallback(async () => {
    try {
      await client.post("/auth/logout");
    } catch {
      // ignore
    }
    setAccessToken(null);
    setUser(null);
    disconnectSocket();
  }, []);

  const updateUser = useCallback((patch: Partial<User>) => {
    setUser((prev) => (prev ? { ...prev, ...patch } : prev));
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, loading, login, login2FA, register, logout, refreshUser, updateUser }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
